import { useCallback } from "react";
import { paths, TemplateHelperInfoBlocks, UnitConfig } from "@helios/toolbox-types";

import { useApi } from "./ApiProvider";
import { initDevice } from "./DevicesProvider";
import {
  Company,
  Config,
  ConfigHistory,
  Device,
  FilteredUnitsResponse,
  Template,
  UnitConfigDefaultResponse,
  UnitConfigHistoryResponse,
  UnitConfigResponse,
  UnitDetails,
  UnitTemplateResponse,
} from "./types";

type WaitForSyncedResponse = paths["/units/{unitid}/config/wait-for-synced/{configId}"]["get"]["responses"]["200"]["content"]["application/json"];

export interface DeviceFilter {
  withTemplate?: boolean;
  unitid?: string;
  hwtype?: string[];
  hwver?: number;
  company?: number[];
  account?: string;
  name?: string;
  description?: string;
}

export const useServerCommunication = () => {
  const api = useApi();

  const getDeviceList = useCallback(
    (params?: DeviceFilter & { limit?: number; joinFwUpdate?: boolean; signal?: AbortSignal }): Promise<Device[]> => {
      const { signal, limit, ...filter } = params ?? {};
      return api<FilteredUnitsResponse>("/units", { method: "POST", body: JSON.stringify({ ...filter, limit: limit ?? 1000 }), signal }).then((res) => {
        if (!res) {
          console.log("WTF?", res);
          throw new Error("?");
        }
        return res.map((item) => {
          let template;
          // try {
          //   template = JSON.parse(item.new_template);
          // } catch (err) {
          template = null;
          // }
          const device: Device = {
            ...initDevice(item.unitid),
            unitDetails: {
              unitid: item.unitid,
              hwtype: item.hwtype,
              hwver: item.hwver,
              swver: item.swver,
            } as UnitDetails,
            company: item.company_ref ? { id: item.company_ref, name: item.company_name as string, shortname: item.company_shortname } : null,
            template,
            accountinfo: { account: item.config_account ?? "", name: item.config_name ?? undefined, description: item.config_description ?? undefined },
            unknownDevice: false,
            update: item.update
              ? {
                  status: item.update.status,
                  status_ts: new Date(item.update.status_ts),
                  retries: item.update.max_failures,
                }
              : undefined,
          };
          return device;
        });
      });
    },
    [api]
  );

  const getUnitDetails = (unitid: number): Promise<UnitDetails | null> => {
    return api<UnitDetails>(`/units/${unitid}/details`).catch((err) => {
      if (err.status === 404) {
        return null;
      }
      throw err;
    });
  };

  const getUnitTemplate = (unitid: number): Promise<Template | null> => {
    return api<UnitTemplateResponse>(`/units/${unitid}/template`).then((res) => {
      if (!res) {
        return null;
      }
      const { id, created_ts, template } = res;
      const createdTs = new Date(created_ts);

      const response: Template = { id, createdTs, template, active: true };
      return response;
    });
  };

  type UnitConfigResponseOrDefault = Pick<
    Omit<UnitConfigResponse, "id" | "modified_ts" | "config" | "modifier_name" | "modifier_fullname"> & {
      id: UnitConfigResponse["id"] | null;
      modified_ts: UnitConfigResponse["modified_ts"] | null;
      config: UnitConfigResponse["config"] | null;
      modifier_name: UnitConfigResponse["modifier_name"] | null;
      modifier_fullname: UnitConfigResponse["modifier_fullname"] | null;
    },
    "id" | "active" | "modified_ts" | "synced_ts" | "config" | "modifier_name" | "modifier_fullname" | "toolbox_config"
  >;
  const processConfig = (resource: UnitConfigResponseOrDefault): Config => {
    const { id, active, modified_ts, synced_ts, config, modifier_name, modifier_fullname, toolbox_config } = resource;
    const processed: Config = {
      id: id ? `${id}` : null,
      active,
      modifiedTs: modified_ts ? new Date(modified_ts) : null,
      syncedTs: synced_ts ? new Date(synced_ts) : null,
      config,
      modifiername: modifier_name,
      modifierfullname: modifier_fullname,
      toolboxConfig: toolbox_config || {},
    };
    return processed;
  };

  const getUnitConfig = (unitid: number): Promise<Config | null> => {
    return new Promise((resolve, reject) => {
      api<UnitConfigResponse>(`/units/${unitid}/config?extend_with_default=true`)
        .then((config) => resolve(processConfig(config)))
        .catch((err) => {
          if (err.status === 404) {
            api<UnitConfigDefaultResponse>(`/units/${unitid}/config/default`).then((toolbox_config) => {
              resolve(
                processConfig({
                  modified_ts: null,
                  active: true,
                  id: null,
                  synced_ts: null,
                  config: null,
                  modifier_name: null,
                  modifier_fullname: null,
                  toolbox_config,
                })
              );
            });
          } else {
            reject(err);
          }
        });
    });
  };

  const getDefaultUnitConfig = (unitid: number) => {
    return api<any>(`/default-config/${unitid}`);
  };

  const getUnitConfigHistory = (unitid: number): Promise<ConfigHistory[]> => {
    return api<UnitConfigHistoryResponse>(`/units/${unitid}/config/history`, { method: "GET" }).then((history) =>
      history.map((item) => {
        const { id, modified_ts, synced_ts, active, modifier_name, modifier_fullname, accountinfo, configVersion } = item;
        return {
          id,
          modifiedTs: new Date(modified_ts),
          syncedTs: synced_ts ? new Date(synced_ts) : null,
          active,
          modifierName: modifier_name,
          modifierFullname: modifier_fullname,
          accountinfo,
          configVersion: configVersion ?? -1,
        };
      })
    );
  };

  const getConfigById = (unitid: number, id: number): Promise<Config | null> => {
    return api<UnitConfig>(`/units/${unitid}/config/id/${id}?extend_with_default=true`).then(
      ({ id, active, config, modified_ts, synced_ts, modifier_name, modifier_fullname, toolbox_config }) => {
        const c: Config = {
          id: `${id}`,
          active,
          config,
          modifiedTs: new Date(modified_ts),
          syncedTs: synced_ts ? new Date(synced_ts) : null,
          modifiername: modifier_name,
          modifierfullname: modifier_fullname,
          toolboxConfig: toolbox_config,
        };
        return c;
      }
    );
  };

  const saveConfig = (unitid: number, currentValue: any): Promise<any> => {
    return api<any>(`/save-config/${unitid}`, { method: "post", body: JSON.stringify(currentValue) });
  };

  const waitForConfigSynced = (configId: number, unitid: number) => {
    return api<WaitForSyncedResponse>(`/units/${unitid}/config/wait-for-synced/${configId}`);
  };

  const getTemplateHelperInfo = (templateId: number, unitid: number) => {
    return api<TemplateHelperInfoBlocks>(`/units/${unitid}/template/${templateId}/info`);
  };

  const getCompanies = () => {
    return api<Company[]>("/companies");
  };

  return {
    getDeviceList,
    getUnitDetails,
    getUnitTemplate,
    getUnitConfig,
    getUnitConfigHistory,
    getDefaultUnitConfig,
    getConfigById,
    saveConfig,
    waitForConfigSynced,
    getTemplateHelperInfo,
    getCompanies,
  };
};
